import { auth, authActions } from "@/services/auth";
import {
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { StateCreator } from "zustand";
import { AuthState, AuthUser, RoleClaims } from "@/types/Auth.ts";
import { UserSlice } from "@/stores/user.slice.ts";
import { user } from "@/repositories/user.ts";
import { CreateUserDto } from "@/models";
import { httpsCallables } from "@/repositories/httpsCallables.ts";
import { toast } from "@/hooks/useToast";
import { sentry } from "@/services/sentry";

export interface AuthSlice {
  initialized: boolean;
  authUser: AuthUser | undefined;
  authState: AuthState;
  authError: string | undefined;
  authRoles: RoleClaims;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  sendVerificationEmail: () => void;
  register: (args: {
    firstname: string;
    lastname: string;
    email: string;
    password: string;
    terms: boolean;
  }) => Promise<boolean>;
  resetPassword: (email: string) => void;
  authListener: () => void;
}

const initialSlice: Pick<
  AuthSlice,
  "initialized" | "authState" | "authError" | "authUser" | "authRoles"
> = {
  initialized: false,
  authUser: undefined,
  authState: "initializing",
  authError: undefined,
  authRoles: { user: false, superAdmin: false, tenants: {} },
};

export const createAuthSlice: StateCreator<
  AuthSlice & UserSlice,
  [],
  [],
  AuthSlice
> = (set, get) => ({
  ...initialSlice,
  authListener: () => {
    auth.onAuthStateChanged(async (user) => {
      set({ initialized: false });
      if (user) {
        const tokenResult = await user.getIdTokenResult();
        get()
          .subscribeUser(user)
          .then(() => {
            set({
              authState: user.emailVerified ? "authenticated" : "unverified",
              authUser: {
                uid: user.uid,
                email: user.email,
                emailVerified: user.emailVerified,
              },
              initialized: true,
              authRoles: tokenResult.claims as RoleClaims,
            });
          });
      } else {
        set({
          ...initialSlice,
          initialized: true,
          user: undefined,
          allowedTenants: undefined,
          authState: "unauthenticated",
          authRoles: undefined,
        });
      }
    });
  },
  login: async (email: string, password: string) => {
    set({ authState: "signingIn", authError: undefined });
    await signInWithEmailAndPassword(auth, email, password)
      .then(async (userCredential) => {
        const authUser = userCredential.user;
        const dbUser = await user.getById(authUser.uid);
        if (!dbUser) {
          throw new Error("User does not exist!");
        }
        await user.update({
          ...dbUser,
          emailVerified: userCredential.user.emailVerified,
          lastLoginTime: new Date(),
        });
        if (!userCredential.user.emailVerified) {
          get().sendVerificationEmail();
        }
        sentry.setUser({
          email: userCredential.user.email ?? "",
          id: userCredential.user.uid,
        });
        set({
          authState: userCredential.user.emailVerified
            ? "authenticated"
            : "unverified",
          authUser: {
            uid: userCredential.user.uid,
            email: userCredential.user.email ?? email,
            emailVerified: userCredential.user.emailVerified,
          },
        });
      })
      .catch((error) => {
        const errorCode = error.code;
        const errorMessage = error.message;
        console.error("Error logging in user:", errorCode, errorMessage);

        const message =
          errorCode === "auth/wrong-password"
            ? "Password is incorrect"
            : errorCode === "auth/user-not-found"
              ? "Email was not found"
              : "Login failed. The email or password is incorrect";
        sentry.setUser({});
        set({
          authError: message,
          authState: "unauthenticated",
        });

        toast({
          title: "Login Error",
          description: message,
          variant: "destructive",
        });
      });
    return;
  },
  logout: () => {
    set({ authState: "signingOut" });
    auth.signOut().then(() => {
      sentry.setUser({});
      set({
        ...initialSlice,
        user: undefined,
        allowedTenants: [],
      });
      window.location.reload();
    });
  },
  sendVerificationEmail: () => {
    set({ authError: undefined });
    if (auth.currentUser) {
      sendEmailVerification(auth.currentUser).then(() => {
        toast({
          title: "Email Verification Sent",
          description: "Click the link in the email to verify your email",
        });
      });
    } else {
      set({ authError: "No user identified to send email verification link." });
    }
    return;
  },
  register: async ({
    firstname,
    lastname,
    email,
    password,
    terms,
  }: {
    firstname: string;
    lastname: string;
    email: string;
    password: string;
    terms: boolean;
  }) => {
    set({ authError: undefined });

    const newUser = await authActions
      .createUserWithEmailAndPassword(email, password)
      .catch((e) => {
        const error =
          e.message === "Firebase: Error (auth/email-already-in-use)."
            ? "Email already in use"
            : e.message;

        set({
          authError: error,
        });
        toast({
          title: "Registration Error",
          description: error,
          variant: "destructive",
        });
      });

    if (!newUser) {
      throw new Error("Unable to create auth user");
    }

    const dbUser: CreateUserDto = {
      id: newUser.user.uid,
      uid: newUser.user.uid,
      email: email,
      displayName: newUser.user.displayName ?? `${firstname} ${lastname}`,
      firstName: firstname,
      lastName: lastname,
      lastLoginTime: new Date(),
      role: "user",
      acceptedTermsAndConditions: terms,
      acceptedTermsAndConditionsTime: new Date(),
    };
    await user.create(dbUser);
    await httpsCallables.updateUserClaims(newUser.user.uid);
    return true;
  },
  resetPassword: async (email: string) => {
    set({ authError: undefined });
    try {
      await sendPasswordResetEmail(auth, email);
      toast({
        title: "Reset Email Sent",
        description: `A password reset email was sent to ${email}`,
      });
    } catch (error) {
      // @ts-expect-error We know this will be the format from Firebase Auth
      const { code, message } = error;
      const errorCode = code;
      const errorMessage = message;
      set({ authError: errorMessage });
      toast({
        title: "Password Reset Error",
        description: errorMessage,
        variant: "destructive",
      });
      console.error(
        "Error sending password reset email:",
        errorCode,
        errorMessage
      );
    }
  },
});
